import ButtonLink from 'components/common/ButtonLink';
import { token } from '@atlaskit/tokens';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import traceUFOPress from '@atlassian/react-ufo/trace-press';

import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ORDER_INVOICE_ACTION_PDF_UFO_NAME } from 'util/ufoNameConstants';

import t from 'modules/i18n/intl';
import config from 'appconfig';
import AmountWithCurrency from 'components/common/AmountWithCurrency';
import { Dimensions } from 'components/visuals';
import Tooltip from 'components/common/Tooltip';

import { fetchInvoiceJSON, fetchInvoicePDF } from '../duck/actions/invoices';
import { messages } from '../messages';
import { StyledSpanWithLineHeight } from './styledComponents';

const Cell = styled.td<{ width: number }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px 0;

  && span {
    line-height: unset;
  }
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const InvoicesActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Dimensions.units(1)}px;
`;

type InvoicesActionLinkType = {
  label: any;
  href?: string;
  disabled?: boolean;
  onClick?: (e: any) => void;
};

const bookedInvoiceStatuses = ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED'];

const getInvoicesActions = (
  rowItem: any,
  handleJSONDownload: (e: any, orderId: string) => void,
  handleInvoiceDownload: (e: any, orderId: string) => void,
): InvoicesActionLinkType[] => {
  const { orderId, processInvoice, status } = rowItem;
  const links: InvoicesActionLinkType[] = [];
  const shouldShowInvoiceLink = !(!processInvoice && !bookedInvoiceStatuses.includes(status));

  links.push({
    label: 'JSON',
    onClick: (e) => handleJSONDownload(e, orderId),
  });

  if (shouldShowInvoiceLink) {
    links.push({
      label: t('mac.orders.table.table-item.action.invoice'),
      onClick: (e) => handleInvoiceDownload(e, orderId),
    });
  }

  return links;
};

const WACLink = (chunks) => <a href={`${config.wacUrl}/my`}>{chunks}</a>;

const StatusTooltip = ({ status }: { status: string }) => {
  const getTooltipContent = () => {
    switch (status) {
      case 'PAID':
        return t(messages.paidInvoiceItemTooltip, { link: WACLink });
      case 'REFUNDED':
        return <FormattedMessage {...messages.refundedItemToolTip} />;
      case 'PARTIALLY_REFUNDED':
        return <FormattedMessage {...messages.partiallyRefundedItemTooltip} />;
      default:
        return <FormattedMessage {...messages.nonDefaultItemTooltip} />;
    }
  };

  return (
    <Tooltip direction="right" content={getTooltipContent()}>
      <StyledSpanWithLineHeight
        $marginLeft={`${Dimensions.units(0.5)}px`}
        $lineHeight={`${Dimensions.units(2)}px`}
      >
        <QuestionCircleIcon
          label={t('mac.invoices.table.table-item.status.tooltip.link') as string}
          size="small"
          color={token('color.text.inverse', '#9FADBC')}
        />
      </StyledSpanWithLineHeight>
    </Tooltip>
  );
};

export const InvoiceCollapsedRow = ({
  showContent,
  rowItem,
}: {
  showContent: boolean;
  rowItem: any;
}) => {
  const { cartOrderDetails, orderNumber, status } = rowItem;
  const dispatch = useDispatch();

  const createdDate = new Date(cartOrderDetails?.created);

  const handleJSONDownload = (e, orderId) => {
    e.stopPropagation();
    dispatch(fetchInvoiceJSON(orderId));
  };

  const handleInvoiceDownload = (e, orderId) => {
    e.stopPropagation();
    traceUFOPress(ORDER_INVOICE_ACTION_PDF_UFO_NAME);
    dispatch(fetchInvoicePDF(orderId));
  };

  const links = getInvoicesActions(rowItem, handleJSONDownload, handleInvoiceDownload);

  return (
    <>
      <Cell width={15} key="invoices-row-sen">
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          {orderNumber}
        </ChevronCellWrapper>
      </Cell>
      <Cell width={15} key="invoices-row-created">
        <FormattedDate value={createdDate} year="numeric" month="short" day="2-digit" />
      </Cell>
      <Cell width={15} key="invoices-row-total">
        <AmountWithCurrency
          amount={cartOrderDetails?.totalIncTax}
          currency={rowItem?.currency || 'USD'}
          weight="400"
        />
      </Cell>
      <Cell width={15} key="invoices-row-status">
        {status} <StatusTooltip status={status} />
      </Cell>
      <Cell width={15} key="invoices-row-actions">
        <InvoicesActionsWrapper>
          {links.map((link) => (
            <ButtonLink key={link.label} disabled={link.disabled} onClick={link.onClick}>
              {link.label}
            </ButtonLink>
          ))}
        </InvoicesActionsWrapper>
      </Cell>
    </>
  );
};
